import {useCallback, useEffect, useState} from 'react';
import {useKeyPress} from '../useKeyPress/useKeyPress';

type ActionType = (index: number | null) => void;

export const useKeyNavigation = ({
  itemCount,
  onEnterAction,
  disabled,
  onNavigationCallback,
}: {
  itemCount: number;
  onEnterAction: ActionType;
  disabled?: boolean;
  onNavigationCallback?: ActionType;
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleOnArrowUpKeyDown = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();

      const newIndex =
        selectedIndex === null || selectedIndex === 0
          ? itemCount - 1
          : selectedIndex - 1;

      onNavigationCallback?.(newIndex);
      setSelectedIndex(newIndex);
    },
    [itemCount, selectedIndex],
  );

  const handleOnArrowDownKeyDown = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();

      const newIndex =
        selectedIndex === null || selectedIndex === itemCount - 1
          ? 0
          : selectedIndex + 1;

      onNavigationCallback?.(newIndex);
      setSelectedIndex(newIndex);
    },
    [itemCount, selectedIndex],
  );

  const handleOnEnterKeyDown = useCallback(
    (e: KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onEnterAction(selectedIndex);
    },
    [selectedIndex, onEnterAction],
  );

  const handleOnEscapeKeyDown = useCallback(() => {
    onNavigationCallback?.(null);
    setSelectedIndex(null);
  }, []);

  useKeyPress({key: 'ArrowUp', onKeyDown: handleOnArrowUpKeyDown, disabled});
  useKeyPress({
    key: 'ArrowDown',
    onKeyDown: handleOnArrowDownKeyDown,
    disabled,
  });
  useKeyPress({key: 'Enter', onKeyDown: handleOnEnterKeyDown, disabled});
  useKeyPress({key: 'Escape', onKeyDown: handleOnEscapeKeyDown, disabled});

  const reset = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [itemCount]);

  return {selectedIndex};
};
